import { InjectionToken, ModuleWithProviders, NgModule } from '@angular/core';
import {
  PIPELINE_TABLE_FILTER_CHIPS_CONFIG_TOKEN,
  PipelineTableFilterChipsConfig,
} from './pipeline-table-filter-chips/tokens';
import { PIPELINE_TABLE_SERVICE_CONFIG_TOKEN, PipelineTableServiceConfig } from './pipeline-table-service/tokens';
import { PIPELINE_SETTINGS_ACCESS_TOKEN, DEFAULT_CURRENCY_CODE_TOKEN, PIPELINE_SETTINGS_URL_TOKEN } from './tokens';
import { USER_PIPELINES_SERVICE_CONFIG_TOKEN, UserPipelinesServiceConfig } from './user-pipelines/tokens';
import { UserPipelinesModule } from './user-pipelines/user-pipelines.module';
import { PipelineStatusCheckboxModule } from './pipeline-status-checkbox/pipeline-status-checkbox.module';
import { PipelineTableModule } from './pipeline-table/pipeline-table.module';
import { PipelineTableComponent } from './pipeline-table/pipeline-table.component';
import { AccountUrlFunction, OpportunityUrlFunction } from '../sales-opportunities/opportunity';
import { LexiconModule } from '@galaxy/lexicon';
import baseTranslation from '../assets/i18n/en_devel.json';
import { WEBLATE_COMPONENT_NAME } from '../shared/constants';
import { PipelineStats } from './pipeline-stats/pipeline-stats.service';
import {
  SALESPERSON_MULTI_SELECT_CONFIG_TOKEN,
  SalespersonMultiSelectConfig,
} from '../filters/salesperson-multi-select/tokens';
import { PACKAGE_SELECT_CONFIG_TOKEN } from '../filters/package-selector/tokens';
import { SALES_TEAM_MULTI_SELECT_CONFIG_TOKEN } from '../filters/salesteam-multi-select/tokens';
import { TAG_MULTI_SELECT_CONFIG_TOKEN } from '../filters/tag-selector/tokens';
import { PRODUCT_SELECTOR_CONFIG_TOKEN } from '../filters/product-selector/tokens';
import { ProductSelectorModule } from '../filters/product-selector/product-selector.module';
import { PotentialRevenueModule } from './pipeline/potential-revenue/potential-revenue.module';
import { REVENUE_DEFAULT_CURRENCY_CODE_TOKEN } from './pipeline/potential-revenue/tokens';
import { PipelineTableConfig } from './pipeline-table/interfaces';
import {
  pipelineSettingsAccessFactory,
  defaultCurrencyCodeFactory,
  packageSelectConfigFactory,
  pipelineSettingsURLFactory,
  productSelectorConfigFactory,
  salesTeamMultiSelectConfigFactory,
  tagMultiSelectConfigFactory,
} from './shared';
import { CLIENT_ORIGIN_TOKEN } from './pipeline/csv/pipeline-export-csv/tokens';
import { ClientOrigin } from '@vendasta/sales-opportunities';

export const PIPELINE_CONFIG_TOKEN = new InjectionToken<PipelineTableConfig>(
  'general dependency data for the pipeline table config',
);

export const OPPORTUNITY_DETAILS_URL_TOKEN: InjectionToken<OpportunityUrlFunction> = new InjectionToken(
  'token for opportunity details URL generator',
);
export const ACCOUNT_DETAILS_URL_TOKEN: InjectionToken<AccountUrlFunction> = new InjectionToken(
  'token for account details URL generator',
);

@NgModule({
  imports: [
    UserPipelinesModule,
    PipelineStatusCheckboxModule,
    PipelineTableModule,
    ProductSelectorModule,
    PotentialRevenueModule,
    LexiconModule.forChild({
      componentName: WEBLATE_COMPONENT_NAME,
      baseTranslation: baseTranslation,
    }),
  ],
  exports: [PipelineTableComponent],
  providers: [PipelineStats],
})
export class PipelinesModule {
  static forRoot(config: InjectionToken<PipelineTableConfig>): ModuleWithProviders<PipelinesModule> {
    return {
      ngModule: PipelinesModule,
      providers: [
        { provide: PIPELINE_CONFIG_TOKEN, useExisting: config, deps: [] },
        { provide: DEFAULT_CURRENCY_CODE_TOKEN, deps: [PIPELINE_CONFIG_TOKEN], useFactory: defaultCurrencyCodeFactory },
        { provide: PIPELINE_SETTINGS_URL_TOKEN, deps: [PIPELINE_CONFIG_TOKEN], useFactory: pipelineSettingsURLFactory },
        {
          provide: PIPELINE_SETTINGS_ACCESS_TOKEN,
          deps: [PIPELINE_CONFIG_TOKEN],
          useFactory: pipelineSettingsAccessFactory,
        },
        {
          provide: REVENUE_DEFAULT_CURRENCY_CODE_TOKEN,
          deps: [PIPELINE_CONFIG_TOKEN],
          useFactory: defaultCurrencyCodeFactory,
        },
        {
          provide: SALESPERSON_MULTI_SELECT_CONFIG_TOKEN,
          deps: [PIPELINE_CONFIG_TOKEN],
          useFactory: salespersonMultiSelectConfigFactory,
        },
        {
          provide: SALES_TEAM_MULTI_SELECT_CONFIG_TOKEN,
          deps: [PIPELINE_CONFIG_TOKEN],
          useFactory: salesTeamMultiSelectConfigFactory,
        },
        {
          provide: PACKAGE_SELECT_CONFIG_TOKEN,
          deps: [PIPELINE_CONFIG_TOKEN],
          useFactory: packageSelectConfigFactory,
        },
        {
          provide: PRODUCT_SELECTOR_CONFIG_TOKEN,
          deps: [PIPELINE_CONFIG_TOKEN],
          useFactory: productSelectorConfigFactory,
        },
        {
          provide: PIPELINE_TABLE_SERVICE_CONFIG_TOKEN,
          deps: [PIPELINE_CONFIG_TOKEN],
          useFactory: pipelineTableServiceConfigFactory,
        },
        {
          provide: TAG_MULTI_SELECT_CONFIG_TOKEN,
          deps: [PIPELINE_CONFIG_TOKEN],
          useFactory: tagMultiSelectConfigFactory,
        },
        {
          provide: PIPELINE_TABLE_FILTER_CHIPS_CONFIG_TOKEN,
          deps: [PIPELINE_CONFIG_TOKEN],
          useFactory: pipelineTableFilterChipsConfigFactory,
        },
        {
          provide: USER_PIPELINES_SERVICE_CONFIG_TOKEN,
          deps: [PIPELINE_CONFIG_TOKEN],
          useFactory: userPipelinesConfigFactory,
        },
        {
          provide: OPPORTUNITY_DETAILS_URL_TOKEN,
          deps: [PIPELINE_CONFIG_TOKEN],
          useFactory: opportunityDetailsUrlGeneratorFactory,
        },
        {
          provide: ACCOUNT_DETAILS_URL_TOKEN,
          deps: [PIPELINE_CONFIG_TOKEN],
          useFactory: accountDetailsUrlGeneratorFactory,
        },
        {
          provide: CLIENT_ORIGIN_TOKEN,
          deps: [PIPELINE_CONFIG_TOKEN],
          useFactory: clientOriginFactory,
        },
      ],
    };
  }
}

function salespersonMultiSelectConfigFactory(config: PipelineTableConfig): SalespersonMultiSelectConfig {
  return {
    allSalespeople$: config.allSalespeople$,
    initWithLoggedInSalesperson: config.initWithLoggedInSalesperson,
    salespersonId$: config.salespersonId$,
    salespersonName$: config.salespersonName$,
  };
}

function pipelineTableServiceConfigFactory(config: PipelineTableConfig): PipelineTableServiceConfig {
  return {
    getOpportunityDetailsUrl: config.getOpportunityDetailsUrl,
    getAccountDetailsUrl: config.getAccountDetailsUrl,
    getCompanyDetailsUrl: config.getCompanyDetailsUrl,
    allSalespeople$: config.allSalespeople$,
    defaultCurrency$: config.defaultCurrency$,
    marketId$: config.marketId$,
    partnerId$: config.partnerId$,
  };
}

function pipelineTableFilterChipsConfigFactory(config: PipelineTableConfig): PipelineTableFilterChipsConfig {
  return {
    hasAccessToAllAccountsInMarket$: config.hasAccessToAllAccountsInMarket$,
    salesTeamsService: config.salesTeamsService,
  };
}

function userPipelinesConfigFactory(config: PipelineTableConfig): UserPipelinesServiceConfig {
  return {
    partnerId$: config.partnerId$,
  };
}

function opportunityDetailsUrlGeneratorFactory(config: PipelineTableConfig): OpportunityUrlFunction {
  return config.getOpportunityDetailsUrl;
}

function accountDetailsUrlGeneratorFactory(config: PipelineTableConfig): AccountUrlFunction {
  return config.getAccountDetailsUrl;
}

function clientOriginFactory(config: PipelineTableConfig): ClientOrigin {
  return config.clientOrigin;
}
